import { parseUnits } from 'ethers';
import JSBI from 'jsbi';

import { EXCHANGE_DECIMALS } from 'config/constants';

import { BigNumberJs, BigNumberClass } from './BigNumber';

// export function createRandomNumber(min: number, max: number): number {
//   return Math.random() * (max - min) + min;
// }

/**
 * Returns the given number of pips as a floating point number with 8 decimals.
 * Examples:
 * BigInt(12345678) => '0.12345678'
 * BigInt(123456789) => '1.23456789'
 * BigInt(100000000) => '1.00000000'
 * BigInt(120000000) => '1.20000000'
 * BigInt(1) => '0.00000001'
 *
 * See also `pipToDecimal` from the SDK.
 */
export function pipToDecimal(pips: JSBI): string {
  const bn = BigNumberJs(pips.toString());
  return bn.shiftedBy(EXCHANGE_DECIMALS * -1).toFixed(EXCHANGE_DECIMALS);
}

export function pipToDecimalSafe(pips: string): string {
  try {
    return pipToDecimal(JSBI.BigInt(pips));
  } catch (error) {
    console.warn(error);
    return '';
  }
}

export function roundDownAtDecimals(
  decimals: number,
  n: string | number
): string {
  const bn = BigNumberJs(n);
  return bn.decimalPlaces(decimals, BigNumberClass.ROUND_DOWN).toString();
}

export function roundUpAtDecimals(
  decimals: number,
  n: string | number
): string {
  const bn = BigNumberJs(n);
  return bn.decimalPlaces(decimals, BigNumberClass.ROUND_CEIL).toString();
}

/**
 * When we multiply two PIPs, we need to reduce twice as much to get decimal
 */
// export function pip2xToDecimal(pips: JSBI): string {
//   const bn = BigNumberJs(pips.toString());
//   return bn.shiftedBy(EXCHANGE_DECIMALS * 2 * -1).toFixed(EXCHANGE_DECIMALS);
// }

/**
 * The exact inverse of pipToDecimal. Truncates anything beyond 8 decimals.
 */
export function decimalToPip(decimal: string): JSBI {
  // Alternativelly formatUnits(pips, EXCHANGE_DECIMALS); but seems it's slower
  const bn = BigNumberJs(decimal);
  return JSBI.BigInt(
    bn
      .shiftedBy(EXCHANGE_DECIMALS)
      .integerValue(BigNumberClass.ROUND_DOWN)
      .toString()
  );
}

// export function maxBigInt(bigints: JSBI[]): JSBI {
//   if (bigints.length === 0) {
//     return JSBI.BigInt(0);
//   }
//   return bigints.reduce(
//     (max, current) => (JSBI.GT(current, max) ? current : max),
//     bigints[0]
//   );
// }

// Error: when on (fixed decimals + 1) It's incorrect result for 0.14999999991105.toFixed(9)
// So need to increase +5 to be more/always correct (?)
// Trim to deicmals without rounding
// export function trimToXDecimals(number: number, decimals: number): number {
//   const stringNumber = Number(number)
//     .toFixed(decimals + 5)
//     .slice(0, -5);
//   return Number(stringNumber);
// }

const truncate8DecimalsSimpleRegex = /^(\d+\.\d{8})\d*$/; // new RegExp('(\\d+\\.\\d{8})(\\d)');

export function trimTo8Decimals(number: string | number): string {
  if (typeof number === 'string') {
    // Optimized way how to do reliable and fast format of string
    const [, afterDot] = number.split('.');
    let finalNumber = number;
    if (afterDot === undefined) {
      finalNumber = number + '.00000000';
    } else if (afterDot?.length < 8) {
      finalNumber = number + '00000000';
    }
    const matched = finalNumber.match(truncate8DecimalsSimpleRegex);
    return matched ? matched[1] : number.toString();
  }
  // To format number properly we use Bignumber
  // which is less performant but super accurate
  return BigNumberJs(number).toFixed(8, BigNumberClass.ROUND_DOWN);

  // .toFormat(8, BigNumberClass.ROUND_DOWN, {
  //   groupSeparator: '',
  //   decimalSeparator: '.',
  // });
  // const matchedNumber = Number(number) //  - 0.000000005)
  //   // First we need to prevent e-10 notation, and then cut remove decimal
  //   // For numbers like 0.000000019 1.9e-8
  //   .toFixed(10)
  //   .toString()
  //   .match(truncate8DecimalsSimpleRegex);
  // return matchedNumber ? matchedNumber[1] : number.toString();
}

/** Format by removing trailing 0's. Round down. */
// export const formatBignumber = (number?: BigNumberClass, maxDecimals = 8) =>
//   number
//     ? number.decimalPlaces(maxDecimals, BigNumberClass.ROUND_DOWN).toString()
//     : '';

const truncateDecimalsInputRegex = [
  /^-?\d+/, // 0 decimals
  /^-?\d+(?:\.\d{0,1})?/, // 1 decimal
  /^-?\d+(?:\.\d{0,2})?/, // 2 decimals
  /^-?\d+(?:\.\d{0,3})?/, // 3 decimals
  /^-?\d+(?:\.\d{0,4})?/, // 4 decimals
  /^-?\d+(?:\.\d{0,5})?/, // 5 decimals
  /^-?\d+(?:\.\d{0,6})?/, // 6 decimals
  /^-?\d+(?:\.\d{0,7})?/, // 7 decimals
  /^-?\d+(?:\.\d{0,8})?/, // 8 decimals
];

const eNotationRegex = /e[+-]/;

/**
 * Sanitize input number and truncate to 8 decimals.
 * Also replace common issues like decimal deparator "," with ".".
 * Disable other characters than numbers.
 */
export const sanitizeAndTruncateNumber = (
  number: string | number,
  decimals = EXCHANGE_DECIMALS
): string => {
  const numberAsStringUncorrected = number.toString();
  const numberAsString =
    // Fix for 10^x e-notation -- transform through BigNumber but good enough for this edge case
    eNotationRegex.test(numberAsStringUncorrected)
      ? BigNumberJs(numberAsStringUncorrected).toString()
      : numberAsStringUncorrected;

  if (decimals > 8) {
    throw new Error('Decimals should be less than 8');
  }
  // // Note that "," is decimal separator in many countries
  // const numberPrepared = numberAsString.includes('.')
  //   ? // Remove thousands separator
  //     number.toString().replace(/,/g, '')
  //   : // Correct "," as decimal separator to "."
  //     number.toString().replace(/,/g, '.');

  // Remove thousands separator (Note: some countries use it as decimal point, but we do not accept such numbers)
  const numberPrepared = numberAsString.replace(/,/g, '');
  return numberPrepared
    ? numberPrepared === '.' // Replace "." by "0." to make typing faster
      ? '0.'
      : numberPrepared.match(truncateDecimalsInputRegex[decimals])?.[0] || ''
    : numberPrepared;
};

export const sanitizeIntegerNumber = (number: string | number): string => {
  const numberPrepared = number.toString().replace('.', '').replace(',', '');
  return numberPrepared
    ? numberPrepared.match(/^\d+$/)?.[0] || ''
    : numberPrepared;
};

export const roundUpAt = (number: number, at: number): number => {
  return Math.ceil(number / at) * at;
};

/**
 * Parse ethers units without throwing errors.
 * If number is unparsable, method will return "undefined" result with Warning
 **/
export const safeParseOfEthersUnits = (
  amount: string,
  decimals: number
): bigint | undefined => {
  try {
    return parseUnits(amount, decimals);
  } catch (error) {
    console.info('Cannot parse units', error?.message);
    return undefined;
  }
};

/** Parse number from text to JSBI */
// export const parseJSBI = (number: string, decimals: number): JSBI =>
//   JSBI.BigInt(ethers.utils.parseUnits(number, decimals));

/** Convert ethers bignumber to JSBI */
// export function ethersBigNumberToJSBI(
//   bigintIsh: ethers.BigNumber | JSBI
// ): JSBI {
//   return bigintIsh instanceof JSBI ? bigintIsh : JSBI.BigInt(bigintIsh);
// }

export const jsbiToBignumberJs = (
  amount: JSBI,
  decimals: number
): BigNumberClass => {
  return BigNumberJs(amount.toString())
    .dividedBy(BigNumberJs(10).pow(decimals))
    .decimalPlaces(decimals, BigNumberClass.ROUND_DOWN);
};

// export const formatNumberWithoutExpNotation = (num: number) =>
//   num.toFixed(10).replace(/\.?0+$/, '');

export function roundDownAndFormatWithTrailingZeros(
  number: BigNumberClass | string | number,
  roundDecimals: number,
  totalDecimals: number
) {
  const roundedNumber = BigNumberJs(number).toFixed(
    roundDecimals,
    BigNumberClass.ROUND_DOWN
  );
  const decimalPart = roundedNumber.split('.')[1] || '';
  const paddedDecimalPart = decimalPart.padEnd(totalDecimals, '0');
  return `${roundedNumber.split('.')[0]}.${paddedDecimalPart}`;
}

/**
 * (123, 10) -> 120
 * (123.4567, 0.001) -> 123.4567
 */
export function truncateNumberBasedOnMultiplier(
  number: BigNumberClass | string | number,
  multiplier: string | number
): string {
  const bigNum = BigNumberJs(number);
  const truncated = bigNum
    .dividedBy(Number(multiplier) || 1)
    .integerValue(BigNumberClass.ROUND_FLOOR)
    .times(Number(multiplier) || 1);
  return truncated.toString();
}

export const isConsideredEmptyOrZero = (value: string) =>
  !value || value === '0';
